/**
 * PublicHeader - 门户头部组件
 * 
 * Apple 风格设计，支持：
 * - 明暗模式切换
 * - 中英文切换
 * - 用户菜单
 * - 管理面板入口
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { 
  Menu, LogIn, X, Settings, User, LogOut, 
  Sun, Moon, Globe2, ChevronDown, PenTool
} from 'lucide-react';
import { supabase } from '../../config/supabase';
import { PermissionLevel } from '../../types';
import { canAccessAdminPanel } from '../../utils/permissions';
import { useI18n } from '../../i18n';
import { useThemeStore } from '../../stores/theme-store';
import { withTimeout } from '../../utils/with-timeout';

const HEADER_AUTH_TIMEOUT_MS = 4000;
const HEADER_AUTH_RETRY_DELAYS_MS = [1_000, 3_000, 8_000];

interface PublicUser {
  id: string;
  email: string;
  role: PermissionLevel;
  name?: string;
  avatar_url?: string;
}

interface PublicHeaderProps {
  toggleSidebar?: () => void;
  sidebarCollapsed?: boolean;
  toggleMobileSidebar: () => void;
  mobileSidebarOpen: boolean;
  onViewChange?: (view: 'files' | 'profile' | 'order') => void;
}

const PublicHeader: React.FC<PublicHeaderProps> = ({
  toggleMobileSidebar,
  mobileSidebarOpen,
  onViewChange
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant } = useParams<{ tenant: string }>();
  const [user, setUser] = useState<PublicUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const authRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // 获取当前租户，默认 'cn'
  const currentTenant = (tenant === 'cn' || tenant === 'intl') ? tenant : 'cn';
  
  // 国际化和主题
  const { t, lang, switchTo } = useI18n();
  const { resolvedTheme, toggleTheme } = useThemeStore();

  useEffect(() => {
    checkAuthentication();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        fetchUserProfile(session.user.id);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setIsAuthenticated(false);
      }
    });

    // 监听头像更新事件
    const handleAvatarUpdate = () => {
      if (user?.id) {
        fetchUserProfile(user.id);
      } else {
        checkAuthentication();
      }
    };
    window.addEventListener('avatar-updated', handleAvatarUpdate);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('avatar-updated', handleAvatarUpdate);
      if (authRetryTimerRef.current) clearTimeout(authRetryTimerRef.current);
    };
  }, [user?.id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showUserMenu && !target.closest('.portal-user-menu-container')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  const scheduleAuthenticationRetry = (attempt: number) => {
    if (attempt >= HEADER_AUTH_RETRY_DELAYS_MS.length) return;
    if (authRetryTimerRef.current) clearTimeout(authRetryTimerRef.current);
    authRetryTimerRef.current = setTimeout(() => {
      authRetryTimerRef.current = null;
      void checkAuthentication(attempt + 1);
    }, HEADER_AUTH_RETRY_DELAYS_MS[attempt]);
  };

  const checkAuthentication = async (attempt = 0) => {
    try {
      const { data: { session } } = await withTimeout(
        supabase.auth.getSession(),
        HEADER_AUTH_TIMEOUT_MS,
        'Portal header session check',
      );
      if (session) {
        await fetchUserProfile(session.user.id, attempt);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.warn('认证检查超时或失败，按游客头部继续:', error);
      setUser(null);
      setIsAuthenticated(false);
      scheduleAuthenticationRetry(attempt);
    }
  };

  const fetchUserProfile = async (userId: string, attempt = 0) => {
    try {
      const { data: profile, error: profileError } = await withTimeout(
        supabase
          .from('v_fs_users')
          .select('id, email, role, name, avatar_url')
          .eq('id', userId)
          .single(),
        HEADER_AUTH_TIMEOUT_MS,
        'Portal header profile lookup',
      );

      if (profileError) {
        throw profileError;
      }
      
      if (profile) {
        if (authRetryTimerRef.current) {
          clearTimeout(authRetryTimerRef.current);
          authRetryTimerRef.current = null;
        }
        setUser(profile as PublicUser);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.warn('获取用户信息超时或失败:', error);
      setUser(null);
      setIsAuthenticated(false);
      scheduleAuthenticationRetry(attempt);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setShowUserMenu(false);
  };

  const handleLanguageSwitch = () => {
    switchTo(lang === 'zh' ? 'en' : 'zh');
  };

  return (
    <header className="portal-header">
      {/* 左侧 - Logo区域 */}
      <div className="flex items-center gap-3">
        {/* 移动端菜单按钮 */}
        <button
          onClick={toggleMobileSidebar}
          className="portal-theme-btn md:hidden"
          aria-label={mobileSidebarOpen ? '关闭菜单' : '打开菜单'}
        >
          {mobileSidebarOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        
        {/* Logo和标题 */}
        <div 
          className="portal-header-logo" 
          onClick={() => navigate('/portal')}
        >
          <span className="portal-header-title">
            {t('portal.title')}
          </span>
        </div>
      </div>

      {/* 右侧控制区域 */}
      <div className="portal-header-actions">
        {/* 主题切换按钮 */}
        <button
          onClick={toggleTheme}
          className="portal-theme-btn"
          aria-label={resolvedTheme === 'dark' ? t('theme.switchToLight') : t('theme.switchToDark')}
          title={resolvedTheme === 'dark' ? t('theme.switchToLight') : t('theme.switchToDark')}
        >
          {resolvedTheme === 'dark' ? (
            <Sun size={18} />
          ) : (
            <Moon size={18} />
          )}
        </button>

        {/* 语言切换按钮 */}
        <button
          onClick={handleLanguageSwitch}
          className="portal-lang-btn hidden sm:flex"
          aria-label={t('switchLanguage')}
          title={t('switchLanguage')}
        >
          <Globe2 size={14} />
          <span>{lang === 'zh' ? '中/EN' : 'EN/中'}</span>
        </button>

        {isAuthenticated && user ? (
          <>
            {/* 管理面板入口按钮 */}
            {canAccessAdminPanel(user.role) && (
              <button
                onClick={() => window.open('/uadmin', '_blank')}
                className="portal-admin-btn hidden sm:flex"
                title={t('portal.adminPanel')}
              >
                <Settings size={16} />
                <span>{t('portal.adminPanel')}</span>
              </button>
            )}
            
            {/* 用户菜单 */}
            <div className="relative portal-user-menu-container ml-2">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="portal-user-btn"
              >
                {user.avatar_url ? (
                  <img 
                    src={user.avatar_url} 
                    alt={user.name || user.email}
                    className="portal-user-avatar"
                  />
                ) : (
                  <div className="portal-user-avatar-placeholder">
                    {(user.name || user.email).charAt(0).toUpperCase()}
                  </div>
                )}
                <ChevronDown size={14} className="text-gray-400 hidden sm:block" />
              </button>

              {/* 下拉菜单 */}
              {showUserMenu && (
                <div className="portal-user-menu">
                  {/* 用户信息区 */}
                  <div className="portal-user-menu-header">
                    <p className="portal-user-menu-name">{user.name || t('profile')}</p>
                    <p className="portal-user-menu-email">{user.email}</p>
                    <span className="portal-user-menu-role">
                      {t(`permissions.${user.role}`)}
                    </span>
                  </div>
                  
                  {/* 菜单项 */}
                  <button
                    onClick={() => {
                      onViewChange?.('profile');
                      setShowUserMenu(false);
                    }}
                    className="portal-user-menu-item"
                  >
                    <User size={16} className="portal-user-menu-item-icon" />
                    {t('portal.userProfile')}
                  </button>

                  <button
                    onClick={() => {
                      window.open('/whiteboard', '_blank', 'noopener,noreferrer');
                      setShowUserMenu(false);
                    }}
                    className="portal-user-menu-item"
                  >
                    <PenTool size={16} className="portal-user-menu-item-icon" />
                    {t('portal.collaborativeWhiteboard')}
                  </button>
                  
                  {canAccessAdminPanel(user.role) && (
                    <button
                      onClick={() => {
                        window.open('/uadmin', '_blank');
                        setShowUserMenu(false);
                      }}
                      className="portal-user-menu-item"
                    >
                      <Settings size={16} className="portal-user-menu-item-icon" />
                      {t('portal.adminPanel')}
                    </button>
                  )}

                  {/* 移动端语言切换 */}
                  <button
                    onClick={() => {
                      handleLanguageSwitch();
                      setShowUserMenu(false);
                    }}
                    className="portal-user-menu-item sm:hidden"
                  >
                    <Globe2 size={16} className="portal-user-menu-item-icon" />
                    {lang === 'zh' ? 'English' : '中文'}
                  </button>
                  
                  <div className="portal-user-menu-divider" />
                  
                  <button
                    onClick={handleSignOut}
                    className="portal-user-menu-item portal-user-menu-item--danger"
                  >
                    <LogOut size={16} />
                    {t('portal.logout')}
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <button
            onClick={() => navigate(`/${currentTenant}/login`, { state: { from: location.pathname } })}
            className="portal-login-btn"
          >
            <LogIn size={16} />
            <span className="hidden sm:inline">{t('portal.loginToSeeMore')}</span>
            <span className="sm:hidden">{t('signIn') || '登录'}</span>
          </button>
        )}
      </div>
    </header>
  );
};

export default PublicHeader;
